import React, { Component, useCallback, useRef, Suspense, useContext, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useToasts, seismicTheme, Theme, Toasts, Button, Spinner, Tooltip, IconDownload, ButtonMenuContainer, ButtonMenuTrigger, ButtonMenuMenu, ButtonMenuItem } from '@seismic/mantle';
import { useTranslation as useTranslation$1, initReactI18next } from 'react-i18next';
import i18n from 'i18next';
import Backend from 'i18next-http-backend/cjs';
import LanguageDetector from 'i18next-browser-languagedetector';
import { getService, CDNService, CommonService, instance, ServiceAddressService, AuthService, TenantService } from '@seismic/browser-services';
import { mergeRefs } from 'react-merge-refs';
import jwt_decode from 'jwt-decode';
import isPlainObject from 'is-plain-obj';

class ErrorBoundary extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            hasError: false
        };
    }
    static getDerivedStateFromError() {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }
    componentDidCatch(error, errorInfo) {
        this.props.onError(error, errorInfo);
    }
    render() {
        return this.props.children;
    }
}

const DownloadToastContext = React.createContext(null);

const CAUGS_SERVICE_KEY = 'caugs';
const TOAST_TIMEOUT = 5000; // From @seismic/mantle
const DOWNLOAD_SIZE_LIMIT = '2GB';
const DEFAULT_FILE_NAME = 'seismic-download';
process.env.NODE_ENV === 'test' ? 'http://localhost:6006' : 'https://studious-memory-738e7b50.pages.github.io';

var CaugsApiEndpoint;
(function (CaugsApiEndpoint) {
    CaugsApiEndpoint["GetFormatsCcmv1"] = "/v1/download/formats";
    CaugsApiEndpoint["GetFormatsCcmv2"] = "/v1/download/:thinContentModel/formats";
    CaugsApiEndpoint["Download"] = "/v1/download";
    CaugsApiEndpoint["BatchDownload"] = "/v1/download/batch";
    CaugsApiEndpoint["QueryDownloadJob"] = "/v1/download/:jobId";
})(CaugsApiEndpoint || (CaugsApiEndpoint = {}));

var ComponentType;
(function (ComponentType) {
    ComponentType["Button"] = "Button";
    ComponentType["MenuItem"] = "MenuItem";
})(ComponentType || (ComponentType = {}));

var ContentVersion;
(function (ContentVersion) {
    ContentVersion[ContentVersion["V1"] = 1] = "V1";
    ContentVersion[ContentVersion["V2"] = 2] = "V2";
})(ContentVersion || (ContentVersion = {}));

var DownloadMode;
(function (DownloadMode) {
    DownloadMode["Single"] = "Single";
    DownloadMode["Batch"] = "Batch";
})(DownloadMode || (DownloadMode = {}));

const DownloadSupportedFileTypes = ['file', 'google', 'article', 'externalvideo', 'htmlarchive', 'folder'];

var DownloadUrlStatus;
(function (DownloadUrlStatus) {
    DownloadUrlStatus["InProgress"] = "InProgress";
    DownloadUrlStatus["Complete"] = "Complete";
    DownloadUrlStatus["Failed"] = "Failed";
})(DownloadUrlStatus || (DownloadUrlStatus = {}));

var ErrorReason;
(function (ErrorReason) {
    // ErrorReasons from the download API
    ErrorReason["SingleDownloadContentNotFound"] = "SingleDownloadContentNotFound";
    ErrorReason["SingleDownloadContentNotSupported"] = "SingleDownloadContentNotSupported";
    ErrorReason["SingleDownloadContentNotAvailable"] = "SingleDownloadContentNotAvailable";
    ErrorReason["SingleDownloadContentWatermarkingFailed"] = "SingleDownloadContentWatermarkingFailed";
    ErrorReason["SingleDownloadContentVideoBssNotReady"] = "SingleDownloadContentVideoBssNotReady";
    ErrorReason["SingleDownloadUnexpected"] = "SingleDownloadUnexpected";
    ErrorReason["BatchDownloadContentNotFound"] = "BatchDownloadContentNotFound";
    ErrorReason["BatchDownloadContentNotSupported"] = "BatchDownloadContentNotSupported";
    ErrorReason["BatchDownloadContentNotAvailable"] = "BatchDownloadContentNotAvailable";
    ErrorReason["BatchDownloadContentWatermarkingFailed"] = "BatchDownloadContentWatermarkingFailed";
    ErrorReason["BatchDownloadContentVideoBssNotReady"] = "BatchDownloadContentVideoBssNotReady";
    ErrorReason["BatchDownloadContentTooLarge"] = "BatchDownloadContentTooLarge";
    ErrorReason["BatchDownloadUnexpected"] = "BatchDownloadUnexpected";
    // Common ErrorReasons
    ErrorReason["GetAvailableFormatsError"] = "GetAvailableFormatsError";
    ErrorReason["GetDownloadUrlError"] = "GetDownloadUrlError";
    ErrorReason["FileDownloadError"] = "FileDownloadError";
    ErrorReason["UnexpectedDownloadError"] = "UnexpectedDownloadError";
})(ErrorReason || (ErrorReason = {}));

var NormalToasts;
(function (NormalToasts) {
    NormalToasts["DownloadInProgress"] = "DownloadInProgress";
    NormalToasts["DownloadComplete"] = "DownloadComplete";
    NormalToasts["DownloadError"] = "DownloadError";
    NormalToasts["BatchDownloadWithUnavailableContent"] = "BatchDownloadWithUnavailableContent";
})(NormalToasts || (NormalToasts = {}));
const ToastsName = { ...NormalToasts, ...ErrorReason };

const ToastsContent = {
    [ToastsName.DownloadInProgress]: 'Error.DownloadInProgress',
    [ToastsName.DownloadComplete]: 'Error.DownloadComplete',
    [ToastsName.DownloadError]: 'Error.DownloadError',
    [ToastsName.SingleDownloadContentNotAvailable]: 'Error.DownloadContentNotAvailable',
    [ToastsName.SingleDownloadContentNotFound]: 'Error.DownloadContentNotFound',
    [ToastsName.SingleDownloadContentNotSupported]: 'Error.DownloadContentNotSupported',
    [ToastsName.SingleDownloadContentVideoBssNotReady]: 'Error.DownloadContentVideoBssNotReady',
    [ToastsName.SingleDownloadContentWatermarkingFailed]: 'Error.DownloadContentWatermarkingFailed',
    [ToastsName.SingleDownloadUnexpected]: 'Error.DownloadUnexpected',
    [ToastsName.BatchDownloadContentNotAvailable]: 'Error.DownloadContentNotAvailable',
    [ToastsName.BatchDownloadContentNotFound]: 'Error.DownloadContentNotFound',
    [ToastsName.BatchDownloadContentNotSupported]: 'Error.DownloadContentNotSupported',
    [ToastsName.BatchDownloadContentTooLarge]: {
        template: 'Error.BatchDownloadContentTooLarge',
        interpolate: { limit: parseInt(DOWNLOAD_SIZE_LIMIT) }
    },
    [ToastsName.BatchDownloadContentVideoBssNotReady]: 'Error.DownloadContentVideoBssNotReady',
    [ToastsName.BatchDownloadContentWatermarkingFailed]: 'Error.DownloadContentWatermarkingFailed',
    [ToastsName.BatchDownloadUnexpected]: 'Error.DownloadUnexpected',
    [ToastsName.BatchDownloadWithUnavailableContent]: 'Error.BatchDownloadWithUnavailableContent'
};
function getToastContent({ name, data, t } = {}) {
    const isError = /error/i.test(name);
    let descriptor = ToastsContent[name];
    if (!descriptor && isError) {
        descriptor = ToastsContent[ToastsName.DownloadError];
    }
    switch (typeof descriptor) {
        case 'object':
            const { template, interpolate } = descriptor;
            return { content: t(template, typeof interpolate === 'function' ? interpolate(data) : interpolate) };
        case 'function':
            return descriptor({ data, t });
        case 'string':
        default: {
            const content = t(descriptor, data);
            return { content };
        }
    }
}

var translations = {
	"Error.BatchDownloadContentTooLarge": "The requested download is too large. It must be under {{limit}}GB.",
	"Error.BatchDownloadWithUnavailableContent": "There's content not available to download.",
	"Error.DownloadComplete": "Download complete.",
	"Error.DownloadError": "Download error.",
	"Error.DownloadInProgress": "Download in progress...",
	"Error.DownloadContentNotAvailable": "{{content}} is not available.",
	"Error.DownloadContentNotFound": "{{content}} is not found.",
	"Error.DownloadContentNotSupported": "Download {{content}} isn't supported.",
	"Error.DownloadContentVideoBssNotReady": "Video {{content}} is being processed, please try downloading again soon.",
	"Error.DownloadContentWatermarkingFailed": "Watermarking for {{content}} failed.",
	"Error.DownloadUnexpected": "Unexpected error occurred at the server side when downloading.",
	"Label.Download": "Download",
	"Tooltip.ContentInvalid": "Content(s) invalid!",
	"Tooltip.SelectContent": "Select content(s)!"
};

function useTranslation(...args) {
    const { t: _t, ...attrs } = useTranslation$1(...args);
    const t = useCallback((key, options) => {
        if (!translations.hasOwnProperty(key)) {
            throw new Error(`Missing translation for key: ${key}`);
        }
        return _t(key, { defaultValue: translations[key], ...options });
    }, [_t]);
    return { t, ...attrs };
}

function useToastsMethods() {
    const { addToast: _addToast, removeToast: _removeToast } = useToasts();
    const toastsRef = useRef([]);
    const { t } = useTranslation();
    const addToast = useCallback((toast = {}) => {
        const { name, data, autoDismiss = true, ...toastProps } = toast;
        if (!name) {
            return;
        }
        // If the toast is already showing, return the dismiss function
        let record = toastsRef.current.find((t) => t.name === name);
        if (record) {
            if (record.timer) {
                clearTimeout(record.timer);
            }
            return record.dismiss;
        }
        // Add the toast
        const id = `download-toast-${Math.random().toString()}`;
        const content = getToastContent({ name, data, t });
        _addToast({ id, autoDismiss, ...content, ...toastProps });
        // Record the toast so we can dismiss it later
        record = { id, name };
        toastsRef.current.push(record);
        // If autoDismiss is false, return a manual dismiss function
        if (autoDismiss !== false) {
            setTimeout(() => {
                toastsRef.current = toastsRef.current.filter((t) => t.id !== id);
            }, TOAST_TIMEOUT);
        }
        else {
            const dismiss = () => {
                const timer = setTimeout(() => {
                    _removeToast(id);
                    toastsRef.current = toastsRef.current.filter((t) => t.id !== id);
                }, TOAST_TIMEOUT);
                record.timer = timer;
            };
            record.dismiss = dismiss;
            return dismiss;
        }
    }, [t] // eslint-disable-line react-hooks/exhaustive-deps
    );
    const removeToast = useCallback((id) => {
        setTimeout(() => {
            _removeToast(id);
            toastsRef.current = toastsRef.current.filter((t) => t.id !== id);
        }, TOAST_TIMEOUT);
    }, [] // eslint-disable-line react-hooks/exhaustive-deps
    );
    const clearToasts = useCallback(() => {
        setTimeout(() => {
            toastsRef.current.forEach((toast) => {
                _removeToast(toast.id);
            });
            toastsRef.current = [];
        }, TOAST_TIMEOUT);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    return { addToast, removeToast, clearToasts };
}

const DownloadToasts = (props) => {
    const { addToast, removeToast, clearToasts } = useToastsMethods();
    return (React.createElement(DownloadToastContext.Provider, { value: { addToast: addToast, removeToast, clearToasts } }, props.children));
};

i18n
    .use(Backend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
    backend: {
        loadPath: async () => {
            let cdn = 'https://cdn-prod.seismic.com';
            try {
                cdn = await getService(CDNService).getI18nCDNURL();
            }
            catch (e) {
                // eslint-disable-next-line no-console
                console.error('Failed to get i18n CDN URL, using default.');
            }
            return `${cdn}/static/seismic-i18n/UniversalDownloadComponent/{{ns}}/{{lng}}.json`;
        },
        crossDomain: true
    },
    debug: true,
    fallbackLng: 'en-US',
    load: 'currentOnly',
    keySeparator: false,
    react: {
        useSuspense: true
    },
    ns: 'Default',
    defaultNS: 'Default'
});

function DownloaderWrap(props) {
    const { children, portalContainer, useExternalTheme } = props;
    const mantleTheme = seismicTheme({
        useGlobal: true,
        customization: {
            colors: {
            // primary: props.globalBranding.primaryColor,
            // primaryVariant: props.globalBranding.primaryVariantColor!
            }
        },
        defaultPortalContainerSelector: '#mntl-portal'
    });
    const core = (React.createElement(Suspense, { fallback: null },
        React.createElement(ErrorBoundary, { onError: (error) => {
                // TODO setup proper error boundary
                // eslint-disable-next-line no-console
                console.log('UDC Error');
                props.onError?.(error);
            } },
            React.createElement(DownloadToasts, null, children))));
    return useExternalTheme ? (core) : (React.createElement(Theme, { theme: mantleTheme },
        React.createElement(Toasts, null, core),
        portalContainer ? ReactDOM.createPortal(React.createElement("div", { id: "mntl-portal" }), portalContainer) : React.createElement("div", { id: "mntl-portal" })));
}

function wrapDownloader(Downloader) {
    const WrappedDownloader = React.forwardRef((props, ref) => {
        const { onError, portalContainer, useExternalTheme = true, nowrap = false, ...downloaderProps } = props;
        const downloader = React.createElement(Downloader, { ...downloaderProps, ref });
        return nowrap
            ? downloader
            : React.createElement(DownloaderWrap, { onError: downloaderProps.downloadOptions?.onError ?? onError, portalContainer, useExternalTheme }, downloader);
    });
    WrappedDownloader.displayName = Downloader.displayName || Downloader.name;
    return WrappedDownloader;
}

function useDownloadToasts() {
    return useContext(DownloadToastContext);
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

function camelizeKeys(obj) {
    if (!isPlainObject(obj))
        return obj;
    return Object.entries(obj).reduce((acc, [key, value]) => {
        const camelizedKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
        return { ...acc, [camelizedKey]: camelizeKeys(value) };
    });
}

class DownloadError extends Error {
    constructor(errorReason, message) {
        super(message ?? '');
        this.name = 'DownloadError';
        this.errorReason = errorReason;
    }
}

function getFilenameFromContentDisposition(contentDisposition = '') {
    const patterns = [/filename="(.+)"/, /filename\*=[^']*'[^']*'(.+)/];
    for (const pattern of patterns) {
        const match = contentDisposition?.match(pattern);
        if (match) {
            return decodeURIComponent(match[1]);
        }
    }
}
function getFilenameFromDownloadUrl(downloadUrl = '') {
    const url = new URL(downloadUrl);
    return url.searchParams.get('fn') ?? url.pathname.split('/').pop();
}
function getDownloadFileName({ downloadUrl, downloadUrlResponse, downloadOptions = {} }) {
    let filename = getFilenameFromContentDisposition(downloadUrlResponse.headers.get('content-disposition') ?? '') ??
        getFilenameFromDownloadUrl(downloadUrl);
    const ext = (filename?.split('.').pop() ??
        (downloadOptions?.mode === DownloadMode.Batch
            ? 'zip'
            : downloadOptions?.format?.includes(':')
                ? downloadOptions.format.split(/\s*:\s*|\*/).join('_')
                : downloadOptions?.format))?.toLowerCase();
    if (!filename || (downloadOptions?.filename && !filename?.includes(downloadOptions.filename))) {
        filename = downloadOptions?.filename ?? DEFAULT_FILE_NAME;
        return ext ? `${filename}.${ext}` : filename;
    }
    return filename;
}
async function downloadFileByObjectUrl(downloadUrl, options) {
    const response = await fetch(downloadUrl);
    // handle any server-side error
    if (!response.ok) {
        throw new DownloadError(ErrorReason.FileDownloadError);
    }
    // download
    const blob = await response.blob();
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    // TODO get file name or add default if zipped
    link.download = getDownloadFileName({
        downloadUrl,
        downloadUrlResponse: response,
        downloadOptions: options
    });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}
async function downloadFile(downloadUrl, options) {
    if (process.env.NODE_ENV === 'test') {
        return downloadFileByObjectUrl(downloadUrl, options);
    }
    try {
        window.open(downloadUrl, '_self');
    }
    catch (e) {
        // If window.open fails to download in some cases, e.g. in cross origin scenario, download the file by object URL
        downloadFileByObjectUrl(downloadUrl, options);
    }
}

function getContentDisplayName(content, options) {
    return (options?.filename ??
        (typeof content === 'string' ? content : content.name ?? content.id));
}

function isBatchDownload(items) {
    return items.length > 1 || (items.length === 1 && items[0]?.type === 'folder');
}

async function poll(url, options) {
    const { method = 'GET', headers, payload, interval, timeout, pollingOn = (res) => res.status === 202 } = options;
    let timer = null;
    async function query() {
        const response = await fetch(url, {
            method: method,
            headers: {
                'Content-Type': 'application/json',
                ...headers
            },
            body: payload ? JSON.stringify(payload) : undefined
        });
        if (timer && (!response.ok || (await pollingOn(response.clone())))) {
            return new Promise((resolve) => setTimeout(async () => resolve(await query()), interval));
        }
        else {
            clearTimeout(timer);
            timer = null;
            return response;
        }
    }
    timer = setTimeout(() => {
        timer = null;
        throw new Error(`polling timeout after ${timeout}s`);
    }, timeout);
    return await query();
}

let DownloadService = class DownloadService {
    get token() {
        const str = this._token?.();
        return str?.replace(/(Bearer|^)\s*/, 'Bearer ');
    }
    get isSetup() {
        return this._isSetup;
    }
    // constructor(sdsService: ServiceAddressService, authService: AuthService) {}
    constructor() {
        this._isSetup = false;
        this.endpoints = Object.entries(CaugsApiEndpoint).reduce((obj, [key, value]) => Object.defineProperty(obj, key, {
            get: () => {
                if (!this._serviceUrl) {
                    throw new Error('DownloadService not setup.');
                }
                return `${this._serviceUrl}${value}`;
            }
        }), {});
        this._sdsService = instance.getService(ServiceAddressService);
        this._authService = instance.getService(AuthService);
        this._tenantService = instance.getService(TenantService);
    }
    isAuthenticated() {
        return this._authService.isAuthenticated() || this.getAccessTokey();
    }
    getAccessTokey() {
        return this._authService.getAccessToken();
    }
    async setupDownload(options = {}) {
        try {
            const { token, serviceUrl } = options;
            let _tenant, _token, _serviceUrl;
            if (this.isAuthenticated()) {
                // If it's authenticated by the common service, the tenant must have been set.
                // We should get the access token, tenant info, and the serviceUrl of CAUGS directly from
                // the common service to prioritize it.
                _tenant = instance.getSubdomain();
                _token = () => this.getAccessTokey();
                _serviceUrl = await this._sdsService.getServiceAddressByKey(CAUGS_SERVICE_KEY);
            }
            else {
                // If the authentication is not done by the common service, we can resolve the tenant
                // info from the jwt token provided, and then can get the serviceUrl by the common service.
                if (token) {
                    _token = () => (typeof token === 'function' ? token() : token);
                }
                if (serviceUrl) {
                    _serviceUrl = serviceUrl;
                }
                else if (_token) {
                    _tenant = jwt_decode(_token())?.tenant;
                    if (!this._isSetup || (_tenant && _tenant !== this._tenant)) {
                        this._tenantService.initTenantInfo(_tenant);
                        _serviceUrl = await this._sdsService.getServiceAddressByKey(CAUGS_SERVICE_KEY);
                    }
                }
            }
            if (_token && _token()) {
                this._token = _token;
                this._tenant = _tenant; // Cache the tenant info to avoid unnecessaryly retrieving the serviceUrl
            }
            if (_serviceUrl) {
                this._serviceUrl = _serviceUrl;
            }
            // If the access token and the serviceUrl are all setup, API calls can be made then.
            if (this.token && this._serviceUrl) {
                this._isSetup = true;
            }
        }
        catch (e) {
            // eslint-disable-next-line no-console
            console.error('DownloadService setup error.', e);
        }
    }
    async getValidFormats(item, downloadOptions) {
        const url = downloadOptions?.contentVersion === ContentVersion.V2
            ? this.endpoints.GetFormatsCcmv2.replace(':thinContentModel', item.serialize())
            : this.endpoints.GetFormatsCcmv1;
        const payload = {
            ApplicationWatermarkData: downloadOptions?.applicationWatermarkData ?? null
        };
        if (downloadOptions?.contentVersion !== ContentVersion.V2) {
            // @ts-ignore
            payload['Content'] = item;
        }
        const response = await fetch(url, {
            method: 'POST',
            // @ts-ignore TODO fix the ts error
            headers: {
                Authorization: this.token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            throw new DownloadError(ErrorReason.GetAvailableFormatsError);
        }
        const data = await response.json();
        return data;
    }
    async pollDownloadUrl(jobId) {
        const response = await poll(this.endpoints.QueryDownloadJob.replace(':jobId', jobId), {
            interval: 10 * 1000,
            timeout: 10 * 60 * 1000,
            headers: {
                Authorization: this.token
            },
            pollingOn: async (res) => (await res.json())?.Status === DownloadUrlStatus.InProgress
        });
        return (await response.json());
    }
    async getDownloadUrl(items, options) {
        if (items.length === 0) {
            throw new Error('No content provided for download');
        }
        const isBatch = options?.mode === DownloadMode.Batch || isBatchDownload(items);
        const contents = options?.contentVersion === ContentVersion.V2
            ? items.map((item) => item.serialize())
            : items;
        // when batch downlad, the ApplicationWatermarkData should be enumerable
        const applicationWatermarkData = isBatch
            ? options?.applicationWatermarkData && !Array.isArray(options?.applicationWatermarkData)
                ? [options?.applicationWatermarkData]
                : options?.applicationWatermarkData ?? []
            : options?.applicationWatermarkData ?? null;
        const payload = {
            [options?.contentVersion === ContentVersion.V2 ? 'Content' : 'ContentV1']: isBatch ? contents : contents[0],
            ApplicationWatermarkData: applicationWatermarkData,
            FileName: options?.filename
        };
        if (!isBatch) {
            Object.assign(payload, {
                Format: options?.format
            });
        }
        const response = await fetch(this.endpoints[isBatch ? 'BatchDownload' : 'Download'], {
            method: 'POST',
            // @ts-ignore TODO fix the ts error
            headers: {
                Authorization: this.token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });
        // Network error or request error
        if (!response.ok) {
            throw new DownloadError(ErrorReason.GetDownloadUrlError);
        }
        let data = (await response.json());
        if (data.Status === DownloadUrlStatus.Complete) {
            return data;
        }
        if (data.Status === DownloadUrlStatus.InProgress && data.Id) {
            data = await this.pollDownloadUrl(data.Id);
            if (data.Status === DownloadUrlStatus.Complete) {
                return data;
            }
        }
        if (data.Status === DownloadUrlStatus.Failed) {
            throw new DownloadError(data.ErrorReason);
        }
        // Unexpected error
        throw new DownloadError(ErrorReason.GetDownloadUrlError);
    }
};
DownloadService = __decorate([
    CommonService('UDC_DownloadService'),
    __metadata("design:paramtypes", [])
], DownloadService);

function useDownloadService() {
    const { addToast } = useDownloadToasts();
    const downloadService = instance.getService(DownloadService);
    const setupDownload = useCallback((options) => {
        downloadService.setupDownload(options);
    }, [downloadService]);
    const getValidFormats = useCallback(async (item, downloadOptions) => {
        return await downloadService.getValidFormats(item, downloadOptions);
    }, [downloadService]);
    const download = useCallback(async (items, options) => {
        if ((await options?.beforeDownload?.(items, options)) === false) {
            return;
        }
        const { Url: downloadUrl, UnavailableContent: unavailableContent } = await downloadService.getDownloadUrl(items, options);
        if (!downloadUrl) {
            throw new DownloadError(ErrorReason.GetDownloadUrlError);
        }
        if (unavailableContent?.length) {
            addToast({
                name: ToastsName.BatchDownloadWithUnavailableContent,
                data: unavailableContent,
                variant: 'warning'
            });
        }
        await options?.onDownloadStart?.(downloadUrl, unavailableContent?.map(camelizeKeys));
        await downloadFile(downloadUrl, {
            ...options,
            mode: options?.mode ?? (isBatchDownload(items) ? DownloadMode.Batch : DownloadMode.Single)
        });
        await options?.onDownloadEnd?.();
    }, [downloadService] // eslint-disable-line react-hooks/exhaustive-deps
    );
    return {
        setupDownload,
        getValidFormats,
        download,
        batchDownload: download
    };
}

function useError() {
    const [_, setError] = useState(null); // eslint-disable-line @typescript-eslint/no-unused-vars
    return useCallback((error) => {
        setError(() => {
            if (error) {
                throw error;
            }
            return null;
        });
    }, []);
}

function useDownloadFormats(items) {
    const setError = useError();
    const [validFormats, setValidFormats] = useState([]);
    const [areFormatsLoading, setAreFormatsLoading] = useState(false);
    const { getValidFormats } = useDownloadService();
    useEffect(() => {
        setValidFormats([]);
    }, [items]);
    async function loadFileDownloadFormats(item, downloadOptions) {
        try {
            setAreFormatsLoading(true);
            const formats = await getValidFormats(item, downloadOptions);
            if (!formats || formats.length === 0) {
                setError(new DownloadError(ErrorReason.GetAvailableFormatsError));
                return [];
            }
            setValidFormats(formats);
            return formats;
        }
        finally {
            setAreFormatsLoading(false);
        }
    }
    return {
        validFormats,
        areFormatsLoading,
        loadFileDownloadFormats
    };
}

function useDownloadHandlers({ loadFileDownloadFormats, onFormatsLoaded, downloadOptions }) {
    const setError = useError();
    const { download, batchDownload } = useDownloadService();
    const { addToast, clearToasts } = useDownloadToasts();
    async function handleDownloadFormats(item) {
        try {
            const dismiss = addToast({ name: ToastsName.DownloadInProgress, autoDismiss: false });
            const formats = await loadFileDownloadFormats(item, downloadOptions);
            dismiss?.();
            onFormatsLoaded(item, formats);
        }
        catch (e) {
            if (!e.errorReason) {
                e = new DownloadError(ErrorReason.UnexpectedDownloadError, e.message);
            }
            clearToasts();
            addToast({ name: e.ErrorReason, variant: 'error' });
            setError(e);
        }
    }
    async function handleSingleFileDownload(item, format) {
        try {
            const dismiss = addToast({ name: ToastsName.DownloadInProgress, autoDismiss: false });
            await download([item], { format, ...downloadOptions });
            dismiss?.();
            addToast({ name: ToastsName.DownloadComplete, variant: 'success' });
        }
        catch (e) {
            if (!e.errorReason) {
                e = new DownloadError(ErrorReason.UnexpectedDownloadError, e.message);
            }
            clearToasts();
            addToast({
                name: e.errorReason,
                variant: 'error',
                data: { content: getContentDisplayName(item, downloadOptions) }
            });
            setError(e);
        }
    }
    async function handleBatchDownload(items) {
        try {
            const dismiss = addToast({ name: ToastsName.DownloadInProgress, autoDismiss: false });
            await batchDownload(items, downloadOptions);
            dismiss?.();
            addToast({ name: ToastsName.DownloadComplete, variant: 'success' });
        }
        catch (e) {
            if (!e.errorReason) {
                e = new DownloadError(ErrorReason.UnexpectedDownloadError, e.message);
            }
            clearToasts();
            addToast({ name: e.errorReason, variant: 'error' });
            setError(e);
        }
    }
    return { handleDownloadFormats, handleSingleFileDownload, handleBatchDownload };
}

function useDownloadSetup(downloadOptions = {}) {
    const { token, serviceUrl } = downloadOptions;
    const { setupDownload } = useDownloadService();
    useEffect(() => {
        setupDownload({ token, serviceUrl });
    }, [token, serviceUrl, setupDownload]);
}

const DownloadButton = React.forwardRef((props, ref) => {
    const { items, downloadOptions, buttonMenuProps, ...buttonProps } = props;
    const { t } = useTranslation();
    // Filters out unsupported items based on the file type in CCM(s)
    const validItems = items.filter((item) => DownloadSupportedFileTypes.includes(item.type?.toLowerCase()));
    const menuButtonRef = useRef(null);
    useDownloadSetup(downloadOptions);
    const { validFormats, areFormatsLoading, loadFileDownloadFormats } = useDownloadFormats(items);
    function onFormatsLoaded(item, formats) {
        if (formats.length <= 1) {
            handleSingleFileDownload(item, formats[0]);
        }
        else {
            menuButtonRef.current?.click();
        }
    }
    const { handleDownloadFormats, handleSingleFileDownload, handleBatchDownload } = useDownloadHandlers({
        loadFileDownloadFormats,
        onFormatsLoaded,
        downloadOptions: downloadOptions
    });
    const renderLoadingButton = () => (
    // @ts-ignore TODO Fix this ts error
    React.createElement(Button, { ref: ref, label: t('Label.Download'), ...buttonProps, startAdornment: React.createElement(Spinner, { size: 16 }), disabled: true, "data-testid": "loading-button" }));
    const renderIdleButton = (toolTipMessage) => (React.createElement(Tooltip, { content: toolTipMessage, "data-testid": "idle-tooltip" },
        React.createElement(Button, { ref: ref, label: t('Label.Download'), startAdornment: React.createElement(IconDownload, { size: 16 }), "data-testid": "idle-button", ...buttonProps, "aria-disabled": true })));
    const renderSingleFileFormatButton = (singleFile) => (
    // @ts-ignore TODO Fix this ts error
    React.createElement(Button, { ref: ref, label: t('Label.Download'), startAdornment: React.createElement(IconDownload, { size: 16 }), ...buttonProps, onClick: () => handleDownloadFormats(singleFile), "data-testid": "single-file-format-button" }));
    const renderSingleFileDefaultDownloadButoon = (singleFile, format) => (
    // @ts-ignore TODO Fix this ts error
    React.createElement(Button, { ref: ref, label: t('Label.Download'), startAdornment: React.createElement(IconDownload, { size: 16 }), ...buttonProps, onClick: () => handleSingleFileDownload(singleFile, format), "data-testid": "single-file-default-download-button" }));
    const renderSingleFileMenuDownloadButton = (singleFile, formats) => (React.createElement(ButtonMenuContainer, { ...buttonMenuProps?.container },
        React.createElement(ButtonMenuTrigger, { ...buttonMenuProps?.trigger },
            React.createElement(Button, { ref: mergeRefs([ref, menuButtonRef]), label: t('Label.Download'), startAdornment: React.createElement(IconDownload, { size: 16 }), ...buttonProps, "data-testid": "single-file-menu-trigger" })),
        React.createElement(ButtonMenuMenu, { ...buttonMenuProps?.menu }, formats.map((format, i) => (React.createElement(ButtonMenuItem, { key: i, onActivate: () => handleSingleFileDownload(singleFile, format), "data-testid": "single-file-menu-item" }, format.toUpperCase()))))));
    // The following JSX will only be rendered when a single valid item is passed in.
    const renderSingleFileDownloadButton = () => {
        const singleFile = validItems[0];
        if (validFormats.length === 0) {
            // This button is rendered when the component is first mounted, or when the passed-in items have changed.
            return renderSingleFileFormatButton(singleFile);
        }
        else if (validFormats.length === 1) {
            // This button will be rendered when valid formats has been fetched and there happen to be only one valid download format
            return renderSingleFileDefaultDownloadButoon(singleFile, validFormats[0]);
        }
        else {
            // This menu is rendered with all available formats displayed when there are multiple valid download formats.
            return renderSingleFileMenuDownloadButton(singleFile, validFormats);
        }
    };
    // The following JSX will only be rendered when multiple items are passed in and at least one of them is downloadable.
    const renderBatchDownloadButton = () => {
        return (
        // @ts-ignore TODO Fix this ts error
        React.createElement(Button, { ref: ref, label: t('Label.Download'), startAdornment: React.createElement(IconDownload, { size: 16 }), ...buttonProps, onClick: () => handleBatchDownload(validItems), "data-testid": "batch-download-button" }));
    };
    // A static loading button will be displayed while fetching formats.
    if (areFormatsLoading)
        return renderLoadingButton();
    return (React.createElement(React.Fragment, null, 
    /**
     * If no items were passed in, or if none of the items are valid,
     * a static idle button will be displayed.
     */
    items.length === 0 || validItems.length === 0
        ? renderIdleButton(`${items.length === 0 ? t('Tooltip.SelectContent') : t('Tooltip.ContentInvalid')}`)
        : // If a single valid item is passed in, the button configured for single file download will be displayed.
            items.length === 1 && !isBatchDownload(validItems)
                ? renderSingleFileDownloadButton()
                : // If multiple items are passed in and at least one of them is valid, the button configured for batch download will be displayed.
                    renderBatchDownloadButton()));
});
DownloadButton.displayName = 'DownloadButton';

const DownloadMenuItem = React.forwardRef((props, ref) => {
    const { items, downloadOptions, ...buttonMenuItemProps } = props;
    const { t } = useTranslation();
    // Filters out unsupported items based on the file type in CCM(s)
    const validItems = items.filter((item) => DownloadSupportedFileTypes.includes(item.type?.toLowerCase()));
    const downloadItemRef = useRef(null);
    useDownloadSetup(downloadOptions);
    const { validFormats, areFormatsLoading, loadFileDownloadFormats } = useDownloadFormats(items);
    function onFormatsLoaded(item, formats) {
        if (formats.length >= 0) {
            handleSingleFileDownload(item, formats[0]);
        }
    }
    const { handleDownloadFormats, handleSingleFileDownload, handleBatchDownload } = useDownloadHandlers({
        loadFileDownloadFormats,
        onFormatsLoaded,
        downloadOptions: downloadOptions
    });
    const renderLoadingButton = () => (React.createElement(ButtonMenuItem, { ref: ref, ...buttonMenuItemProps, startAdornment: React.createElement(Spinner, { size: 16 }), disabled: true, "data-testid": "loading-button" }, buttonMenuItemProps.children ?? t('Label.Download')));
    const renderIdleButton = (toolTipMessage) => (React.createElement(Tooltip, { content: toolTipMessage, "data-testid": "idle-tooltip" },
        React.createElement(ButtonMenuItem, { ref: ref, startAdornment: React.createElement(IconDownload, { size: 16 }), ...buttonMenuItemProps, disabled: true, "aria-disabled": true, "data-testid": "idle-button" }, buttonMenuItemProps.children ?? t('Label.Download'))));
    const renderSingleFileInitDownload = (singleFile) => (React.createElement(ButtonMenuItem, { ref: ref, startAdornment: React.createElement(IconDownload, { size: 16 }), ...buttonMenuItemProps, "data-testid": "single-file-format-button", onActivate: () => handleDownloadFormats(singleFile) }, buttonMenuItemProps.children ?? t('Label.Download')));
    const renderSingleFileDefaultDownload = (singleFile, format) => (React.createElement(ButtonMenuItem, { ref: mergeRefs([ref, downloadItemRef]), startAdornment: React.createElement(IconDownload, { size: 16 }), ...buttonMenuItemProps, 
        // @ts-ignore TODO Fix this ts error
        onActivate: () => handleSingleFileDownload(singleFile, format), "data-testid": "single-file-default-download-button" }, buttonMenuItemProps.children ?? t('Label.Download')));
    // The following JSX will only be rendered when a single valid item is passed in.
    const renderSingleFileDownload = () => {
        const singleFile = validItems[0];
        if (validFormats.length === 0) {
            // This button is rendered when the component is first mounted, or when the passed-in items have changed.
            return renderSingleFileInitDownload(singleFile);
        }
        else {
            // This button will be rendered when valid formats has been fetched and there happen to be only one valid download format
            return renderSingleFileDefaultDownload(singleFile, validFormats[0]);
        }
    };
    // The following JSX will only be rendered when multiple items are passed in and at least one of them is downloadable.
    const renderBatchDownload = () => {
        return (React.createElement(ButtonMenuItem, { ref: ref, startAdornment: React.createElement(IconDownload, { size: 16 }), ...buttonMenuItemProps, onActivate: () => handleBatchDownload(validItems), "data-testid": "batch-download-button" }, buttonMenuItemProps.children ?? t('Label.Download')));
    };
    // A static loading button will be displayed while fetching formats.
    if (areFormatsLoading)
        return renderLoadingButton();
    return (React.createElement(React.Fragment, null, 
    /**
     * If no items were passed in, or if none of the items are valid,
     * a static idle button will be displayed.
     */
    items.length === 0 || validItems.length === 0
        ? renderIdleButton(`${items.length === 0 ? t('Tooltip.SelectContent') : t('Tooltip.ContentInvalid')}`)
        : // If a single valid item is passed in, the button configured for single file download will be displayed.
            items.length === 1 && !isBatchDownload(validItems)
                ? renderSingleFileDownload()
                : // If multiple items are passed in and at least one of them is valid, the button configured for batch download will be displayed.
                    renderBatchDownload()));
});
DownloadMenuItem.displayName = 'DownloadMenuItem';

/**
 * The Universal Download Component allows downloading of Seismic content given common content models.
 * This is a shared component that helps with unifying the overall Seismic content for content from a single source.
 * This wraps Mantle's Button component and accepts all [Mantle Button props](https://mantle-ui.seismic.com/#/Forms/Button).
 * @see README for usage https://github.com/seismic/web-universal-download-component-assets/blob/master/README.md
 * @param props @see {@link UniversalDownloadComponentProps}
 */
const UniversalDownloadComponent = React.forwardRef((props, ref) => {
    const { componentAs, onError, nowrap = false, portalContainer, useExternalTheme, ...downloaderProps } = props;
    let Downloader = null;
    switch (componentAs) {
        case ButtonMenuItem:
        case ComponentType.MenuItem:
            Downloader = DownloadMenuItem;
            break;
        case Button:
        case ComponentType.Button:
            Downloader = DownloadButton;
            break;
        default:
            Downloader = componentAs;
    }
    const downloader = Downloader && React.createElement(Downloader, { ...downloaderProps, ref });
    return nowrap ? (downloader) : (React.createElement(DownloaderWrap, { onError: downloaderProps.downloadOptions?.onError ?? onError, portalContainer: portalContainer, useExternalTheme: useExternalTheme }, downloader));
});
UniversalDownloadComponent.displayName = 'UniversalDownloadComponent';
UniversalDownloadComponent.defaultProps = {
    componentAs: Button,
    useExternalTheme: true
};

async function download(items, downloadOptions) {
    try {
        const itemsArr = Array.isArray(items) ? items : [items];
        if (!items || itemsArr.length === 0) {
            throw new Error('No items provided to download.');
        }
        const downloadService = instance.getService(DownloadService);
        await downloadService.setupDownload(downloadOptions);
        if (!downloadService.isSetup) {
            throw new Error('DownloadService not setup correctly.');
        }
        if ((await downloadOptions?.beforeDownload?.(itemsArr, downloadOptions)) === false) {
            return;
        }
        if (downloadOptions?.mode !== DownloadMode.Batch && !isBatchDownload(itemsArr)) {
            const formats = await downloadService.getValidFormats(itemsArr[0], downloadOptions);
            if (!formats?.length) {
                throw new Error('No valid formats found for this item.');
            }
            Object.assign(downloadOptions, { format: formats[0] });
        }
        const { Url: downloadUrl, UnavailableContent: unavailableContent } = await downloadService.getDownloadUrl(itemsArr, downloadOptions);
        if (!downloadUrl) {
            throw new Error('Failed to get the download URL for this item.');
        }
        await downloadOptions?.onDownloadStart?.(downloadUrl, unavailableContent?.map(camelizeKeys));
        await downloadFile(downloadUrl, {
            ...downloadOptions,
            mode: downloadOptions?.mode ?? (isBatchDownload(itemsArr) ? DownloadMode.Batch : DownloadMode.Single)
        });
        await downloadOptions?.onDownloadEnd?.();
    }
    catch (e) {
        downloadOptions?.onError?.(e);
        // eslint-disable-next-line no-console
        console.error(`Failed to download the item with error -- ${e.name}: ${e.message}.`);
        throw e;
    }
}

// It would be nice to `export { Foo, IFoo } from './Foo'` but re-exporting types
const WrappedDownloadButton = wrapDownloader(DownloadButton);
const WrappedDownloadMenuItem = wrapDownloader(DownloadMenuItem);

export { ComponentType, WrappedDownloadButton as DownloadButton, WrappedDownloadMenuItem as DownloadButtonMenuItem, UniversalDownloadComponent, download };
//# sourceMappingURL=index.js.map
